import {
  ChannelClient,
  createServiceIdentifier,
  createServiceProxy,
  type IMessageTransport,
  InstantiationService,
} from '@hibro/platform-core';
import {
  applyTheme,
  blockFileDragThroughOverlayHost,
  createInlineDetachedStore,
  DetachedSurface,
  GateHost,
  I18nProvider,
  type IframeBridge,
  type InAppNotificationBridge,
  type InlineDetachedStore,
  installEscapeToCloseWindow,
  installNavigationGuard,
  isSettingsWindowSurface,
  KeybindingHost,
  type LinkedModulesBridge,
  type LoadUi,
  type LogBridge,
  ModuleShellHost,
  mountCaptureWindow,
  type OpenExternalBridge,
  type OsNotificationBridge,
  OVERLAY_PORTAL_HOST_ID,
  parseSurfaceTarget,
  type SurfaceTarget,
  UserActivationProvider,
  type WindowControlsBridge,
} from '@hibro/platform-renderer';
import {
  type ActiveGate,
  APP_LINKS_CHANNEL,
  CONFIG_CHANGED_EVENT,
  CONFIG_CHANNEL,
  type ConfigChange,
  ESCAPE_CLOSES_WINDOW_KEY,
  GATE_CHANGED_EVENT,
  GATE_CHANNEL,
  type GateChanged,
  type HostOsThemeFlavor,
  hostOsThemeFlavor,
  type IAppLinkService,
  type IConfigurationService,
  type IGateService,
  type IKeybindingService,
  type ILauncherService,
  type IModuleService,
  type INlsService,
  type IOfflineService,
  type IOverlayService,
  type IPartitionService,
  type IPermissionService,
  type IProductConfigService,
  type ISearchRecentService,
  type IThemeService,
  type IUpdateService,
  type IWhenContextService,
  KEYBINDING_CHANNEL,
  LAUNCHER_CHANNEL,
  LIGHT_THEME,
  MODULE_CHANNEL,
  type ModuleRuntime,
  NLS_CHANNEL,
  OFFLINE_CHANNEL,
  OVERLAY_CHANNEL,
  parseShellTarget,
  PARTITION_CHANNEL,
  PERMISSION_CHANNEL,
  PRODUCT_CONFIG_CHANNEL,
  readHostOs,
  SEARCH_RECENT_CHANNEL,
  type ShellTarget,
  THEME_CHANGED_EVENT,
  THEME_CHANNEL,
  type ThemeChange,
  type ThemeDefinition,
  UPDATE_CHANNEL,
  type UserActivationApi,
  WHEN_CONTEXT_CHANNEL,
  windowCanvasColor,
  WORKBENCH_CALLER_ID,
} from '@hibro/shared';
import { Spinner } from '@hibro/ui-client';
import { type ReactNode, StrictMode } from 'react';
import * as ReactNS from 'react';
import * as ReactJsxDevRuntime from 'react/jsx-dev-runtime';
import * as ReactJsxRuntime from 'react/jsx-runtime';
import * as ReactDomNS from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';

import type { PickLocalBundle } from './bundle-bridge';
import { createDetachBridge } from './detach-bridge';
import { EmbeddedPlatformSettings } from './embedded-platform-settings';
import { createExternalBridge } from './external-bridge';
import { createIframeBridge } from './iframe-bridge';
import { createElectronLogBridge } from './log-bridge';
import { getNavigationRegistry } from './navigation-registry';
import { createWindowControlsBridge } from './window-controls-bridge';
import { WorkbenchHost } from './workbench-host';

// Per-process DI identifiers for the host services (the cross-process contract
// is the channel name; the symbols are local to this renderer).

/*
 * OS-derived platform flavor for the Mac/Win split tokens (见 13 / §5.10).
 * `window.hibroHost` is stamped by each runtime's shell before `bootShell`, so
 * it is available on the first synchronous `applyTheme(LIGHT_THEME)`. Linux and
 * unknown hosts fall back to the mac flavor (the CSS default), matching the
 * wallet module's existing `data-platform` resolution.
 */
const stampedOs = readHostOs();
const themePlatform: HostOsThemeFlavor = stampedOs
  ? hostOsThemeFlavor(stampedOs)
  : 'mac';

/**
 * Paint the native window brush with this theme's canvas (见 08). The
 * synchronous light first frame does not call this — that would flash a light
 * brush over a dark preference before `getTheme()` returns. The web runtime
 * has no native window, so the bridge is absent and this is a no-op.
 */
function syncWindowCanvas(theme: ThemeDefinition): void {
  const win = window.hibroWindow;
  if (!win) {
    return;
  }
  let color: string;
  try {
    color = windowCanvasColor(theme);
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    window.hibroLog?.append({
      level: 'error',
      kind: 'log',
      scope: 'shell',
      message: `window canvas color is not paintable: ${reason}`,
    });
    return;
  }
  void win.setBackgroundColor(color).catch((err: unknown) => {
    const reason = err instanceof Error ? err.message : String(err);
    window.hibroLog?.append({
      level: 'error',
      kind: 'log',
      scope: 'shell',
      message: `failed to set window background: ${reason}`,
    });
  });
}

const IModuleServiceId =
  createServiceIdentifier<IModuleService>('IModuleService');
const IConfigurationServiceId = createServiceIdentifier<IConfigurationService>(
  'IConfigurationService',
);
const IProductConfigServiceId = createServiceIdentifier<IProductConfigService>(
  'IProductConfigService',
);
const IPermissionServiceId =
  createServiceIdentifier<IPermissionService>('IPermissionService');
const IOverlayServiceId =
  createServiceIdentifier<IOverlayService>('IOverlayService');
const IOfflineServiceId =
  createServiceIdentifier<IOfflineService>('IOfflineService');
const IThemeServiceId = createServiceIdentifier<IThemeService>('IThemeService');
const INlsServiceId = createServiceIdentifier<INlsService>('INlsService');
const IGateServiceId = createServiceIdentifier<IGateService>('IGateService');
const ILauncherServiceId =
  createServiceIdentifier<ILauncherService>('ILauncherService');
const IAppLinkServiceId =
  createServiceIdentifier<IAppLinkService>('IAppLinkService');
const ISearchRecentServiceId = createServiceIdentifier<ISearchRecentService>(
  'ISearchRecentService',
);
const IWhenContextServiceId = createServiceIdentifier<IWhenContextService>(
  'IWhenContextService',
);
const IPartitionServiceId =
  createServiceIdentifier<IPartitionService>('IPartitionService');
const IUpdateServiceId =
  createServiceIdentifier<IUpdateService>('IUpdateService');
const IKeybindingServiceId =
  createServiceIdentifier<IKeybindingService>('IKeybindingService');

export interface ShellBootOptions {
  /** Where the workbench mounts (the `#root` element). */
  container: HTMLElement;
  /**
   * Which runtime this shell is (见 18 / 19). Named by the entry rather than
   * sniffed: the two facts that depend on it — whether OS-level chords exist at
   * all, and whether the browser keeps some of them for itself (见 §5.9) — must
   * not be guessed from a user-agent string.
   */
  runtime: ModuleRuntime;
  /**
   * How `iframe` views get their gated SDK port (见 9 / 18). Defaults to the
   * Electron bridge (preload-brokered); the browser runtime injects one that
   * mints the `MessageChannel` in-page and transfers it to the SharedWorker.
   */
  iframeBridge?: IframeBridge;
  /**
   * Observability sink behind the diagnostics section, crash toast and console
   * mirror (见 15 / §5.16). Defaults to the Electron bridge (preload → the
   * main-process sink); the browser runtime injects a page-side one. When neither
   * is available those three surfaces stay unmounted.
   */
  logBridge?: LogBridge;
  /**
   * OS-notification permission (见 18 / §5.15). Injected only by the browser
   * runtime, where the grant has to be requested inside a user gesture and so
   * needs a settings control; the desktop's notifications come from the main
   * process and need no grant, so it leaves this absent.
   */
  osNotificationBridge?: OsNotificationBridge;
  /**
   * In-app notification delivery (见 23). Injected only by the browser runtime:
   * on Electron the workbench host reads the same contract off the preload
   * `window.hibroNotification` global, so nothing has to be threaded through here.
   */
  inAppNotifications?: InAppNotificationBridge;
  /**
   * Load-unpacked dev modules (见 11 调试 / 18). Injected only by the browser
   * runtime: on Electron the same verbs already ride on the main-process
   * devtools bridge, which the workbench host reads off `window.hibroDevtools`.
   */
  linkedModulesBridge?: LinkedModulesBridge;
  /**
   * Page-side picker for settings「从 .tgz / 从目录」(见 12 / 18). Injected
   * only by the browser runtime so the click is still a live gesture when
   * the File System Access picker opens.
   */
  pickLocalBundle?: PickLocalBundle;
  /**
   * Sync open for the browser runtime (见 21 / 18). Keeps the user gesture so
   * the popup blocker does not reject `openUrl` launches. Desktop leaves this
   * absent and falls back to `ILauncherService.openExternal`.
   */
  openExternalBridge?: OpenExternalBridge;
  /**
   * Trusted source-UI actions that must begin in the renderer's current user
   * gesture. Both runtimes provide the same API; Web uses it to pre-open a
   * browser shell before any asynchronous handoff.
   */
  userActivation?: UserActivationApi;
  /**
   * Renderer-side UI entries per module id, discovered by each runtime's entry
   * with its own `import.meta.glob` (延续 见 02 B.4: `modules/*` references stay
   * out of the platform packages).
   */
  uiLoaders: Map<string, LoadUi>;
  /**
   * Record a shell-level diagnostic (见 15 / §5.16). Electron routes this to the
   * main-process sink via `window.hibroLog`; the browser runtime keeps its own.
   */
  log?: (message: string) => void;
}

/**
 * The runtime-agnostic half of the app shell boot.
 *
 * Everything here is identical on Electron and in the browser (见 18): assemble
 * the per-connection DI container of RPC proxies, apply + follow the theme,
 * report connectivity, and route between the three top-level surfaces (app gate
 * / detached single surface / full workbench). What differs is only *how the
 * transport is obtained* — a preload-brokered `MessagePort` on desktop, a
 * SharedWorker port in the browser — so each runtime entry owns that and calls
 * {@link connect} with the resulting transport.
 *
 * `connect` is callable repeatedly: every call tears down the previous client
 * and rebuilds the container, so a host crash/restart (desktop) or a worker
 * reconnect (web) resumes transparently.
 */
export function bootShell(options: ShellBootOptions): {
  connect: (transport: IMessageTransport) => void;
  /** Replace the tree with the connecting placeholder (used before a reconnect). */
  showConnecting: () => void;
  /**
   * Replace the tree with a host-lost screen (见 18). The browser SharedWorker
   * is not supervised the way the desktop utilityProcess is, so a dead host
   * leaves the page stuck on "Connecting…" unless something explicit is shown.
   */
  showDisconnected: (message: string, onRetry: () => void) => void;
  /**
   * Detached surfaces this page is showing itself, for a runtime that cannot
   * always open a window (见 18 弹窗拦截). Handed back rather than injected
   * because the caller both fills it (when a popup is refused) and reads it (to
   * tell the host a frame closed); Electron never touches it, which is why the
   * store is created unconditionally but stays empty there.
   */
  inlineDetached: InlineDetachedStore;
} {
  const overlayHost = document.getElementById(OVERLAY_PORTAL_HOST_ID);
  if (overlayHost) {
    // The host covers the app tree. A file drag that hits it must not be
    // retargeted through the portal into the covered surface.
    blockFileDragThroughOverlayHost(overlayHost);
  }

  const captureDisplayId = new URLSearchParams(window.location.search).get(
    'capture',
  );
  if (captureDisplayId) {
    // The workbench applies this just before its first render (见 13). This
    // branch returns first, and `main.css` has no `--app-base-*`. The editor
    // resolves ink colors from those primitives, so they have to be on the
    // document before it mounts. This page never connects a host, so the
    // light palette stays; the toolbar is a light bar either way.
    applyTheme(LIGHT_THEME, undefined, themePlatform);
    mountCaptureWindow(options.container, captureDisplayId);
    return {
      connect: () => undefined,
      showConnecting: () => undefined,
      showDisconnected: () => undefined,
      inlineDetached: createInlineDetachedStore(),
    };
  }

  const { container, uiLoaders, log, runtime } = options;
  const iframeBridge = options.iframeBridge ?? createIframeBridge();
  const inlineDetached = createInlineDetachedStore();
  const bridges: WorkbenchBridges = {
    logBridge: options.logBridge ?? createElectronLogBridge(),
    // No Electron default: the desktop has no page-level notification grant to
    // ask for, so leaving it absent is the answer, not a missing implementation.
    osNotificationBridge: options.osNotificationBridge,
    inAppNotifications: options.inAppNotifications,
    linkedModulesBridge: options.linkedModulesBridge,
    pickLocalBundle: options.pickLocalBundle,
    openExternalBridge: options.openExternalBridge,
    userActivation: options.userActivation,
  };

  // Expose the renderer's React singletons so dynamically delivered source-module
  // UIs (loaded at runtime via the import map, 见 12) share this exact React
  // instance. Without this their hooks would read a second React's null
  // dispatcher ("Invalid hook call"). The runtime shim modules re-export these.
  window.__hibroReact = ReactNS;
  window.__hibroReactDom = ReactDomNS;
  window.__hibroJsxRuntime = ReactJsxRuntime;
  window.__hibroJsxDevRuntime = ReactJsxDevRuntime;

  installRafResizeObserver();

  // A `detach` window loads this same entry with `?surface=<viewId>&module=<id>`
  // (架构方案 §5.8). The built-in browser host uses `?shell=browser` (见 22).
  // Surface detach keeps priority over shell mode when both are present.
  const surfaceTarget: SurfaceTarget | null = parseSurfaceTarget(
    window.location.search,
  );
  const shellTarget: ShellTarget | null = surfaceTarget
    ? null
    : parseShellTarget(window.location.search);

  // Paint the built-in light theme synchronously before the first render (见 13):
  // `getTheme()` below is an IPC round trip, so without this the first frames have
  // no `--app-base-*` at all — `main.css` only inlines the flattened
  // `--app-color-*` layer. Any rule referencing a primitive (module token layers
  // such as IM's `--im-*`, or `bg-(--app-base-…)` call sites) would be invalid at
  // computed-value time and paint colorless. The resolved theme replaces this the
  // moment it lands, so a dark preference still only shows the same light first
  // frame it does today.
  applyTheme(LIGHT_THEME, undefined, themePlatform);

  const root = createRoot(container);
  renderConnecting(root);

  let currentClient: ChannelClient | undefined;
  // Report connectivity transitions to the host (§5.14); rebinds to the live
  // proxy across reconnects via `currentOffline`.
  let currentOffline: IOfflineService | undefined;
  const reportOnline = (): void => {
    void currentOffline?.reportNetworkStatus(navigator.onLine);
  };
  window.addEventListener('online', reportOnline);
  window.addEventListener('offline', reportOnline);

  // Keep the workbench document from being navigated away, routing the address
  // through `openLink` instead (见 20 / 06). Installed on the window rather than
  // per connection — the guard has to hold while the host is down too, or a
  // reconnect gap would be a hole — so it reads the live proxy through
  // `currentModuleService`, the same shape `reportOnline` uses above.
  let currentModuleService: IModuleService | undefined;
  installNavigationGuard({
    openLink: (url) => {
      const service = currentModuleService;
      if (!service?.openLink) {
        // Rejecting rather than resolving `false`: "no host to ask" and "no
        // module claimed it" are different outcomes, and the guard has already
        // swallowed the navigation, so the log is the only place this can show.
        return Promise.reject(
          new Error('the extension host is not connected to this window'),
        );
      }
      return service.openLink(url, WORKBENCH_CALLER_ID);
    },
    ...(log ? { log } : {}),
  });

  // One bridge per page rather than one per render pass: `WindowControls`
  // subscribes to `onMaximizeChange` keyed on its identity, and the Escape
  // handler below outlives every reconnect, so a fresh object each time would
  // re-subscribe for nothing and leave the handler pointing at a stale one.
  const windowControlsBridge = createWindowControlsBridge();

  // Escape closes this window when nothing in the page claimed the key (见 08).
  // Installed on the window like the navigation guard above — it must survive a
  // host restart — so the switch is read through a live variable the connection
  // below keeps current.
  //
  // It starts *off* rather than at the schema default: the value arrives one RPC
  // after connect, and of the two ways to be wrong for that moment, "Escape does
  // nothing yet" is recoverable while "the window a user disabled this for just
  // vanished" is not. That is the opposite resolution from `closeToTray`'s
  // startup gap (见 23), because there the unrecoverable side is quitting.
  let escapeClosesWindow = false;
  if (windowControlsBridge && windowControlsBridge.chrome !== 'none') {
    installEscapeToCloseWindow({
      close: () => windowControlsBridge.close(),
      isEnabled: () => escapeClosesWindow,
    });
  }

  const connect = (transport: IMessageTransport): void => {
    currentClient?.close();
    const client = new ChannelClient(transport);
    currentClient = client;

    // Assemble a per-connection DI container and register each host service as
    // an auto-generated RPC proxy (架构方案 §5.1 / §5.5).
    const services = new InstantiationService();
    services.registerInstance(
      IModuleServiceId,
      createServiceProxy<IModuleService>(client, MODULE_CHANNEL),
    );
    services.registerInstance(
      IConfigurationServiceId,
      createServiceProxy<IConfigurationService>(client, CONFIG_CHANNEL),
    );
    services.registerInstance(
      IProductConfigServiceId,
      createServiceProxy<IProductConfigService>(client, PRODUCT_CONFIG_CHANNEL),
    );
    services.registerInstance(
      IPermissionServiceId,
      createServiceProxy<IPermissionService>(client, PERMISSION_CHANNEL),
    );
    services.registerInstance(
      IOverlayServiceId,
      createServiceProxy<IOverlayService>(client, OVERLAY_CHANNEL),
    );
    services.registerInstance(
      IOfflineServiceId,
      createServiceProxy<IOfflineService>(client, OFFLINE_CHANNEL),
    );
    services.registerInstance(
      IThemeServiceId,
      createServiceProxy<IThemeService>(client, THEME_CHANNEL),
    );
    services.registerInstance(
      INlsServiceId,
      createServiceProxy<INlsService>(client, NLS_CHANNEL),
    );
    services.registerInstance(
      IGateServiceId,
      createServiceProxy<IGateService>(client, GATE_CHANNEL),
    );
    services.registerInstance(
      ILauncherServiceId,
      createServiceProxy<ILauncherService>(client, LAUNCHER_CHANNEL),
    );
    services.registerInstance(
      IAppLinkServiceId,
      createServiceProxy<IAppLinkService>(client, APP_LINKS_CHANNEL),
    );
    services.registerInstance(
      ISearchRecentServiceId,
      createServiceProxy<ISearchRecentService>(client, SEARCH_RECENT_CHANNEL),
    );
    services.registerInstance(
      IWhenContextServiceId,
      createServiceProxy<IWhenContextService>(client, WHEN_CONTEXT_CHANNEL),
    );
    services.registerInstance(
      IPartitionServiceId,
      createServiceProxy<IPartitionService>(client, PARTITION_CHANNEL),
    );
    services.registerInstance(
      IUpdateServiceId,
      createServiceProxy<IUpdateService>(client, UPDATE_CHANNEL),
    );
    services.registerInstance(
      IKeybindingServiceId,
      createServiceProxy<IKeybindingService>(client, KEYBINDING_CHANNEL),
    );

    // The renderer is the source of truth for connectivity (§5.14): report the
    // current `navigator.onLine` now and on every change, so the host broadcasts
    // a single shared network status to all windows.
    currentOffline = services.get(IOfflineServiceId);
    void currentOffline.reportNetworkStatus(navigator.onLine);

    // Keep the Escape-closes-window switch current for every shell this entry
    // can render — detached and shell windows return early below, and they are
    // windows too. Re-read on each reconnect for the same reason the theme is.
    const configService = services.get(IConfigurationServiceId);
    void configService.getValue(ESCAPE_CLOSES_WINDOW_KEY).then((value) => {
      escapeClosesWindow = value === true;
    });
    client.listen(CONFIG_CHANGED_EVENT, (data) => {
      const change = data as ConfigChange;
      if (change.key === ESCAPE_CLOSES_WINDOW_KEY) {
        escapeClosesWindow = change.value === true;
      }
    });

    // Apply the effective theme now and on every change (见 13 / §5.10).
    // Tokens are set as `--app-*` CSS variables on the document root; the
    // THEME_CHANGED_EVENT broadcast (preference / accent edit or OS scheme flip) repaints
    // every window. Re-fetched on each reconnect so a host restart re-syncs.
    const themeService = services.get(IThemeServiceId);
    void themeService.getTheme().then((t) => {
      applyTheme(t, undefined, themePlatform);
      syncWindowCanvas(t);
    });
    client.listen(THEME_CHANGED_EVENT, (data) => {
      const theme = (data as ThemeChange).theme;
      applyTheme(theme, undefined, themePlatform);
      syncWindowCanvas(theme);
    });

    const nlsService = services.get(INlsServiceId);
    const gateService = services.get(IGateServiceId);
    const moduleService = services.get(IModuleServiceId);
    currentModuleService = moduleService;

    // Every top-level surface below gets the same keybinding wiring (§5.9).
    // Chords used to be installed by the workbench alone, so a detached window
    // or a module shell had none at all (见 08 已知缺口); mounting the host out
    // here is what makes "which surface am I" a `when` key instead of a
    // difference in where the listener lives.
    const keybindings: KeybindingWiring = {
      moduleService,
      keybindingService: services.get(IKeybindingServiceId),
      client,
      runtime,
      dev: import.meta.env.DEV,
      ...(log ? { log } : {}),
    };

    if (surfaceTarget) {
      // Detached windows (§5.8) are single-surface shells and not subject to the
      // app gate; render the popped-out surface directly.
      renderDetachedSurface(
        root,
        surfaceTarget,
        client,
        moduleService,
        nlsService,
        uiLoaders,
        iframeBridge,
        windowControlsBridge,
        keybindings,
        bridges.userActivation,
        configService,
        themeService,
        isSettingsWindowSurface(
          surfaceTarget.viewId,
          surfaceTarget.fragmentId,
        ) ? (
          <EmbeddedPlatformSettings
            client={client}
            configService={configService}
            permissionService={services.get(IPermissionServiceId)}
            launcherService={services.get(ILauncherServiceId)}
            appLinkService={services.get(IAppLinkServiceId)}
            searchRecentService={services.get(ISearchRecentServiceId)}
            logBridge={bridges.logBridge}
            linkedModulesBridge={bridges.linkedModulesBridge}
            osNotificationBridge={bridges.osNotificationBridge}
            pickLocalBundle={bridges.pickLocalBundle}
          />
        ) : undefined,
        {
          updateService: services.get(IUpdateServiceId),
          launcherService: services.get(ILauncherServiceId),
          productConfigService: services.get(IProductConfigServiceId),
          openExternalBridge: bridges.openExternalBridge,
          whenContextService: services.get(IWhenContextServiceId),
        },
      );
      return;
    }

    if (shellTarget) {
      // Module-contributed top-level shell: the platform supplies connection,
      // shared chrome and view activation; the owning module paints the body.
      renderModuleShell(
        root,
        shellTarget,
        client,
        moduleService,
        nlsService,
        uiLoaders,
        windowControlsBridge,
        keybindings,
        log,
        bridges.userActivation,
      );
      return;
    }

    // App gate (架构方案 §5.8): an active gate view full-screen replaces the
    // workbench (which is then never mounted); no gate → the full workbench.
    const showMain = (gate: ActiveGate | null): void => {
      log?.(
        `app gate: ${gate ? `render gate ${gate.id}` : 'render workbench'}`,
      );
      if (gate) {
        renderGate(
          root,
          gate,
          client,
          moduleService,
          nlsService,
          uiLoaders,
          iframeBridge,
          windowControlsBridge,
          keybindings,
          bridges.userActivation,
        );
      } else {
        renderWorkbench(
          root,
          client,
          uiLoaders,
          iframeBridge,
          inlineDetached,
          bridges,
          keybindings,
          {
            moduleService,
            configService,
            productConfigService: services.get(IProductConfigServiceId),
            permissionService: services.get(IPermissionServiceId),
            overlayService: services.get(IOverlayServiceId),
            themeService,
            nlsService,
            launcherService: services.get(ILauncherServiceId),
            appLinkService: services.get(IAppLinkServiceId),
            searchRecentService: services.get(ISearchRecentServiceId),
            updateService: services.get(IUpdateServiceId),
            whenContextService: services.get(IWhenContextServiceId),
            partitionService: services.get(IPartitionServiceId),
          },
        );
      }
    };
    // Read the current gate now (it may have been raised by an onStartup module
    // before this renderer connected) and re-decide on every change (login /
    // logout). Mirrors the theme read-then-subscribe idiom above; re-run on each
    // reconnect so a host restart re-syncs the gate.
    void gateService.getActiveGate().then(showMain);
    client.listen(GATE_CHANGED_EVENT, (data) => {
      showMain((data as GateChanged).gate);
    });
  };

  return {
    connect,
    showConnecting: () => renderConnecting(root),
    showDisconnected: (message, onRetry) =>
      renderDisconnected(root, message, onRetry),
    inlineDetached,
  };
}

/**
 * Source-level fix for the benign "ResizeObserver loop …" warnings (见
 * troubleshooting.md, w3c/csswg-drafts#5023). The warning is emitted synchronously
 * when an RO callback mutates layout and thereby re-enters the observation cycle in
 * the *same* frame — intrinsic to virtualized lists' dynamic `measureElement`
 * (`@tanstack/react-virtual`), whose observer we can't reach directly. Wrapping the
 * global `ResizeObserver` so every delivery is deferred to the next animation frame
 * breaks the synchronous re-entry, so the loop is never detected and no warning
 * fires. The one-frame defer is imperceptible (rows first paint at the tuned
 * `estimateSize`; the RO only applies a tiny post-mount height correction).
 *
 * Installed before React renders, so all observers — app shell / dockview /
 * every module — are created through the wrapper.
 */
function installRafResizeObserver(): void {
  if (typeof window.ResizeObserver === 'undefined') {
    return;
  }
  const Native = window.ResizeObserver;
  window.ResizeObserver = class extends Native {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        // Deliver on the next frame so the callback runs *outside* the RO cycle:
        // any layout it mutates starts a fresh cycle later instead of re-entering
        // this one (which is what emits the warning). Each native batch keeps its
        // own rAF — RO reports only the newly-changed entries per delivery and never
        // re-sends them, so we must not drop/coalesce batches or we'd lose measures.
        requestAnimationFrame(() => {
          callback(entries, observer);
        });
      });
    }
  };
}

function renderConnecting(root: Root): void {
  root.render(
    <StrictMode>
      <div className="flex h-full w-full items-center justify-center">
        <Spinner className="size-6 text-(--app-color-text-muted)" />
      </div>
    </StrictMode>,
  );
}

function renderDisconnected(
  root: Root,
  message: string,
  onRetry: () => void,
): void {
  root.render(
    <StrictMode>
      <div style={{ fontFamily: 'system-ui, sans-serif', padding: 16 }}>
        <p>{message}</p>
        <button type="button" onClick={onRetry}>
          Retry
        </button>
      </div>
    </StrictMode>,
  );
}

interface WorkbenchServices {
  moduleService: IModuleService;
  configService: IConfigurationService;
  productConfigService: IProductConfigService;
  permissionService: IPermissionService;
  overlayService: IOverlayService;
  themeService: IThemeService;
  nlsService: INlsService;
  launcherService: ILauncherService;
  appLinkService: IAppLinkService;
  searchRecentService: ISearchRecentService;
  updateService: IUpdateService;
  whenContextService: IWhenContextService;
  partitionService: IPartitionService;
}

/**
 * The optional per-runtime bridges the workbench takes. Grouped rather than
 * appended one positional at a time: they all answer "does this runtime have
 * X", so a call site reading `…, logBridge, osNotificationBridge, services)`
 * would be noise where a named object is self-describing.
 */
interface WorkbenchBridges {
  logBridge?: LogBridge;
  osNotificationBridge?: OsNotificationBridge;
  inAppNotifications?: InAppNotificationBridge;
  linkedModulesBridge?: LinkedModulesBridge;
  pickLocalBundle?: PickLocalBundle;
  openExternalBridge?: OpenExternalBridge;
  userActivation?: UserActivationApi;
}

/**
 * What every surface needs to own a keybinding table (§5.9). Grouped rather than
 * appended one positional at a time: the four render functions below differ in
 * what they render, not in how chords are wired.
 */
interface KeybindingWiring {
  moduleService: IModuleService;
  keybindingService: IKeybindingService;
  client: ChannelClient;
  runtime: ModuleRuntime;
  dev: boolean;
  log?: (message: string) => void;
}

function renderWorkbench(
  root: Root,
  client: ChannelClient,
  uiLoaders: Map<string, LoadUi>,
  iframeBridge: IframeBridge,
  inlineDetached: InlineDetachedStore,
  bridges: WorkbenchBridges,
  keybindings: KeybindingWiring,
  services: WorkbenchServices,
): void {
  root.render(
    <StrictMode>
      <UserActivationProvider api={bridges.userActivation}>
        <I18nProvider nlsService={services.nlsService} client={client}>
          <KeybindingHost {...keybindings} surface="workbench">
            <WorkbenchHost
              iframeBridge={iframeBridge}
              inlineDetached={inlineDetached}
              logBridge={bridges.logBridge}
              osNotificationBridge={bridges.osNotificationBridge}
              inAppNotifications={bridges.inAppNotifications}
              linkedModulesBridge={bridges.linkedModulesBridge}
              pickLocalBundle={bridges.pickLocalBundle}
              openExternalBridge={bridges.openExternalBridge}
              client={client}
              moduleService={services.moduleService}
              configService={services.configService}
              productConfigService={services.productConfigService}
              permissionService={services.permissionService}
              overlayService={services.overlayService}
              themeService={services.themeService}
              nlsService={services.nlsService}
              launcherService={services.launcherService}
              appLinkService={services.appLinkService}
              searchRecentService={services.searchRecentService}
              updateService={services.updateService}
              whenContextService={services.whenContextService}
              partitionService={services.partitionService}
              uiLoaders={uiLoaders}
            />
          </KeybindingHost>
        </I18nProvider>
      </UserActivationProvider>
    </StrictMode>,
  );
}

/**
 * Application root gate (架构方案 §5.8 App Gate): while a gate is active it
 * full-screen replaces the workbench — the `Workbench` is never mounted — so
 * pre-gate (e.g. before login/workspace) the user cannot see or enter it. A
 * `source` module owns the policy of when to raise/release it via `context.gate`.
 */
function renderGate(
  root: Root,
  gate: ActiveGate,
  client: ChannelClient,
  moduleService: IModuleService,
  nlsService: INlsService,
  uiLoaders: Map<string, LoadUi>,
  iframeBridge: IframeBridge,
  windowControlsBridge: WindowControlsBridge | undefined,
  keybindings: KeybindingWiring,
  userActivation?: UserActivationApi,
): void {
  root.render(
    <StrictMode>
      <UserActivationProvider api={userActivation}>
        <I18nProvider nlsService={nlsService} client={client}>
          <KeybindingHost {...keybindings} surface="gate">
            <GateHost
              gate={gate}
              moduleService={moduleService}
              uiLoaders={uiLoaders}
              client={client}
              iframeBridge={iframeBridge}
              navigation={getNavigationRegistry()}
              windowControlsBridge={windowControlsBridge}
            />
          </KeybindingHost>
        </I18nProvider>
      </UserActivationProvider>
    </StrictMode>,
  );
}

function renderModuleShell(
  root: Root,
  target: ShellTarget,
  client: ChannelClient,
  moduleService: IModuleService,
  nlsService: INlsService,
  uiLoaders: Map<string, LoadUi>,
  windowControlsBridge: WindowControlsBridge | undefined,
  keybindings: KeybindingWiring,
  log?: (message: string) => void,
  userActivation?: UserActivationApi,
): void {
  root.render(
    <StrictMode>
      <UserActivationProvider api={userActivation}>
        <I18nProvider nlsService={nlsService} client={client}>
          <KeybindingHost {...keybindings} surface="shell">
            <ModuleShellHost
              shellId={target.mode}
              moduleService={moduleService}
              uiLoaders={uiLoaders}
              client={client}
              navigation={getNavigationRegistry()}
              windowControlsBridge={windowControlsBridge}
              onError={log}
            />
          </KeybindingHost>
        </I18nProvider>
      </UserActivationProvider>
    </StrictMode>,
  );
}

function renderDetachedSurface(
  root: Root,
  target: SurfaceTarget,
  client: ChannelClient,
  moduleService: IModuleService,
  nlsService: INlsService,
  uiLoaders: Map<string, LoadUi>,
  iframeBridge: IframeBridge,
  windowControlsBridge: WindowControlsBridge | undefined,
  keybindings: KeybindingWiring,
  userActivation?: UserActivationApi,
  configService?: IConfigurationService,
  themeService?: IThemeService,
  developerSettings?: ReactNode,
  about?: {
    updateService?: IUpdateService;
    launcherService?: ILauncherService;
    productConfigService?: IProductConfigService;
    openExternalBridge?: OpenExternalBridge;
    whenContextService?: IWhenContextService;
  },
): void {
  const detachBridge = createDetachBridge(client);
  root.render(
    <StrictMode>
      <UserActivationProvider api={userActivation}>
        <I18nProvider nlsService={nlsService} client={client}>
          <KeybindingHost {...keybindings} surface="detached">
            <DetachedSurface
              viewId={target.viewId}
              moduleId={target.moduleId}
              fragmentId={target.fragmentId}
              instanceKey={target.instanceKey}
              payload={target.payload}
              chrome={target.chrome}
              moduleService={moduleService}
              configService={configService}
              nlsService={nlsService}
              themeService={themeService}
              uiLoaders={uiLoaders}
              iframeBridge={iframeBridge}
              externalBridge={createExternalBridge()}
              client={client}
              navigation={getNavigationRegistry()}
              windowControlsBridge={windowControlsBridge}
              detachBridge={detachBridge}
              developerSettings={developerSettings}
              updateService={about?.updateService}
              launcherService={about?.launcherService}
              productConfigService={about?.productConfigService}
              openExternalBridge={about?.openExternalBridge}
              whenContextService={about?.whenContextService}
            />
          </KeybindingHost>
        </I18nProvider>
      </UserActivationProvider>
    </StrictMode>,
  );
}
